/**
 * useMenuState.ts - Menu State Management Hook
 * 
 * React hook for menu state management with OpCentral orchestrator integration
 * 
 * ARCHITECTURE:
 * - Uses @opcentral/cachr for state persistence (NOT localStorage)
 * - Uses @opcentral/actr for action logging
 * - Uses @opcentral/backtrackr for undo/redo
 * 
 * CHANGE HISTORY:
 * - 251115_1630 [current] danielbeer: Migrated from Interfacr with Cachr integration
 */

import { useState, useEffect, useCallback } from 'react';
import { useCachr } from '@opcentral/cachr';
import { useBacktrackr } from '@opcentral/backtrackr';
import type { MenuState, MenuPersistedState } from './types';

export interface UseMenuStateOptions {
  appName: string;
  menuId: string;
  persistState?: boolean;
  defaultCollapsed?: boolean;
  onStateChange?: (state: MenuState) => void;
}

export function useMenuState(options: UseMenuStateOptions) {
  const {
    appName,
    menuId,
    persistState = false,
    defaultCollapsed = false,
    onStateChange
  } = options;
  
  const { get, set } = useCachr();
  const backtrackr = useBacktrackr() as any;
  
  // Initialize state
  const [state, setState] = useState<MenuState>(() => {
    if (persistState) {
      try {
        const stored = get(`menu_${appName}_${menuId}`);
        if (stored) {
          actr?.execute('menu:state-loaded', {
            appName,
            menuId,
            hasCollapsed: stored.collapsed !== undefined,
            hasExpanded: stored.expandedSections?.length > 0
          });
          
          return {
            collapsed: stored.collapsed,
            expandedItems: stored.expandedSections || [],
            selectedItem: undefined,
            searchQuery: undefined,
            customOrder: stored.customOrder || [],
            hiddenItems: stored.hiddenItems || []
          };
        }
      } catch (error) {
        console.error('[useMenuState] Failed to load stored state:', error);
        actr?.execute('menu:state-load-error', {
          appName,
          menuId,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
    
    return {
      collapsed: defaultCollapsed,
      expandedItems: [],
      selectedItem: undefined,
      searchQuery: undefined,
      customOrder: [],
      hiddenItems: []
    };
  });
  
  // Save state to Cachr
  const saveState = useCallback((newState: Partial<MenuState>) => {
    const previousState = state;
    const updated = { ...state, ...newState };
    setState(updated);
    
    if (persistState) {
      try {
        const persistedState: MenuPersistedState = {
          menuId,
          collapsed: updated.collapsed,
          expandedSections: updated.expandedItems,
          pinnedItems: [],
          customOrder: updated.customOrder || [],
          hiddenItems: updated.hiddenItems || [],
          recentItems: [],
          preferences: {
            variant: 'default',
            showIcons: true,
            showBadges: true,
            showShortcuts: true
          },
          lastUpdated: Date.now()
        };
        
        // Save to Cachr
        set(`menu_${appName}_${menuId}`, persistedState);
        
        // Log action
        actr?.execute('menu:state-saved', {
          appName,
          menuId,
          changes: Object.keys(newState)
        });
        
        // Record for undo/redo
        if (backtrackr) {
          backtrackr.recordChange({
            type: 'menu:state-update',
            description: `Update menu state: ${Object.keys(newState).join(', ')}`,
            context: null,
            before: previousState,
            after: updated,
            metadata: { appName, menuId }
          });
        }
      } catch (error) {
        console.error('[useMenuState] Failed to save state:', error);
        actr?.execute('menu:state-save-error', {
          appName,
          menuId,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
  }, [appName, menuId, state, persistState, set, actr, backtrackr]);
  
  // Notify on state change
  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);
  
  // Toggle collapsed
  const toggleCollapsed = useCallback(() => {
    const newCollapsed = !state.collapsed;
    
    actr?.execute('menu:toggle-collapsed', {
      appName,
      menuId,
      collapsed: newCollapsed
    });
    
    saveState({ collapsed: newCollapsed });
  }, [state.collapsed, appName, menuId, actr, saveState]);
  
  // Set collapsed
  const setCollapsed = useCallback((collapsed: boolean) => {
    actr?.execute('menu:set-collapsed', {
      appName,
      menuId,
      collapsed
    });
    
    saveState({ collapsed });
  }, [appName, menuId, actr, saveState]);
  
  // Toggle item expanded
  const toggleItemExpanded = useCallback((itemId: string) => {
    const isExpanded = state.expandedItems.includes(itemId);
    const newExpandedItems = isExpanded
      ? state.expandedItems.filter(id => id !== itemId)
      : [...state.expandedItems, itemId];
    
    actr?.execute('menu:toggle-item-expanded', {
      appName,
      menuId,
      itemId,
      expanded: !isExpanded
    });
    
    saveState({ expandedItems: newExpandedItems });
  }, [state.expandedItems, appName, menuId, actr, saveState]);
  
  // Set item expanded
  const setItemExpanded = useCallback((itemId: string, expanded: boolean) => {
    const isExpanded = state.expandedItems.includes(itemId);
    
    if (expanded && !isExpanded) {
      saveState({ expandedItems: [...state.expandedItems, itemId] });
    } else if (!expanded && isExpanded) {
      saveState({ expandedItems: state.expandedItems.filter(id => id !== itemId) });
    }
  }, [state.expandedItems, saveState]);
  
  // Select item
  const selectItem = useCallback((itemId: string) => {
    actr?.execute('menu:select-item', {
      appName,
      menuId,
      itemId
    });
    
    setState(prev => ({ ...prev, selectedItem: itemId }));
  }, [appName, menuId, actr]);
  
  // Set search query
  const setSearchQuery = useCallback((query: string) => {
    actr?.execute('menu:search', {
      appName,
      menuId,
      query
    });
    
    setState(prev => ({ ...prev, searchQuery: query }));
  }, [appName, menuId, actr]);
  
  // Clear search
  const clearSearch = useCallback(() => {
    actr?.execute('menu:clear-search', {
      appName,
      menuId
    });
    
    setState(prev => ({ ...prev, searchQuery: undefined }));
  }, [appName, menuId, actr]);
  
  // Expand all
  const expandAll = useCallback((itemIds: string[]) => {
    actr?.execute('menu:expand-all', {
      appName,
      menuId,
      count: itemIds.length
    });
    
    saveState({ expandedItems: itemIds });
  }, [appName, menuId, actr, saveState]);
  
  // Collapse all
  const collapseAll = useCallback(() => {
    actr?.execute('menu:collapse-all', {
      appName,
      menuId
    });
    
    saveState({ expandedItems: [] });
  }, [appName, menuId, actr, saveState]);
  
  // Reorder items
  const reorderItems = useCallback((fromIndex: number, toIndex: number, itemIds: string[]) => {
    const newOrder = [...itemIds];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    
    actr?.execute('menu:reorder-items', {
      appName,
      menuId,
      from: fromIndex,
      to: toIndex,
      itemId: removed
    });
    
    saveState({ customOrder: newOrder });
  }, [appName, menuId, actr, saveState]);
  
  // Hide item
  const hideItem = useCallback((itemId: string) => {
    const newHiddenItems = [...(state.hiddenItems || []), itemId];
    
    actr?.execute('menu:hide-item', {
      appName,
      menuId,
      itemId
    });
    
    saveState({ hiddenItems: newHiddenItems });
  }, [state.hiddenItems, appName, menuId, actr, saveState]);
  
  // Show item
  const showItem = useCallback((itemId: string) => {
    const newHiddenItems = (state.hiddenItems || []).filter(id => id !== itemId);
    
    actr?.execute('menu:show-item', {
      appName,
      menuId,
      itemId
    });
    
    saveState({ hiddenItems: newHiddenItems });
  }, [state.hiddenItems, appName, menuId, actr, saveState]);
  
  // Reset state
  const resetState = useCallback(() => {
    actr?.execute('menu:reset-state', {
      appName,
      menuId
    });
    
    setState({
      collapsed: defaultCollapsed,
      expandedItems: [],
      selectedItem: undefined,
      searchQuery: undefined,
      customOrder: [],
      hiddenItems: []
    });
    
    if (persistState) {
      try {
        set(`menu_${appName}_${menuId}`, null);
      } catch (error) {
        console.error('[useMenuState] Failed to clear state:', error);
      }
    }
  }, [appName, menuId, defaultCollapsed, persistState, set, actr]);
  
  return {
    state,
    toggleCollapsed,
    setCollapsed,
    toggleItemExpanded,
    setItemExpanded,
    selectItem,
    setSearchQuery,
    clearSearch,
    expandAll,
    collapseAll,
    reorderItems,
    hideItem,
    showItem,
    resetState
  };
}
